import { Link } from 'react-router-dom';
import {
    ArchitectureDiagram,
    CardGrid,
    ContentSection,
    ExecutionPathDiagram,
    LinkRow,
    PageFrame,
    PageHero,
    ProofBlock,
    SystemLayerStack,
    TruthStateGrid,
} from '../components/PageBlocks';
import { CLAIM_TRUTH_CLASSES, getPageConfig } from '../data/page-config.mjs';

const page = getPageConfig('/technology');

const TechnologyPage = () => {
    return (
        <PageFrame>
            <PageHero
                sectionId="overview"
                variant="split"
                numeral="III"
                eyebrow={page.routePurpose}
                title={page.h1}
                intro="AvenBox is not being presented as magic. It is being presented as a real control architecture: local execution as the default, orchestration over where work runs, continuity across trusted environments, and explicit external paths only when they are chosen."
                primaryCta={{ label: 'See Pricing', to: '/pricing' }}
                secondaryCta={{ label: 'Contact', to: '/contact', variant: 'secondary' }}
                proofStrip={[
                    'Architecture today, not theater.',
                    'Local-first execution with explicit routing.',
                    'Continuity and trusted extension are part of the system.',
                ]}
            />

            <ContentSection
                sectionId="architecture-overview"
                eyebrow="Architecture Overview"
                title="AvenBox is built on five system layers. Each enforces the control model at a different point in the execution path."
                intro="The architecture is the claim. This page shows the design logic."
                borderTop={false}
            >
                <ArchitectureDiagram
                    caption="AvenBox sits between user devices and all execution paths. Local intelligence, orchestration, trust boundary, and continuity layers enforce the control model at every routing decision."
                />
                <SystemLayerStack
                    layers={[
                        { label: 'Local intelligence', detail: 'Default execution layer', accent: true },
                        { label: 'Orchestration', detail: 'Routing and coordination' },
                        { label: 'Trust boundary', detail: 'Privacy enforcement' },
                        { label: 'Continuity', detail: 'Device + app + workstation' },
                        { label: 'Trusted extension', detail: 'Private infrastructure scaling' },
                    ]}
                    caption="Five system layers from local execution to trusted extension. Each layer enforces the control model."
                />
                <div className="ed-meta-wrap ed-meta-wrap--3x2">
                <CardGrid
                    pattern="meta"
                    items={[
                        {
                            title: 'Local intelligence layer',
                            body: 'Suitable tasks should run close to the user first, which preserves privacy, lowers dependency, and anchors the system in local control.',
                        },
                        {
                            title: 'Orchestration layer',
                            body: 'AvenBox is a control layer because it has to decide what runs where, which path is appropriate, and when any outside dependency is even justified.',
                        },
                        {
                            title: 'Trust-boundary layer',
                            body: 'The architecture matters because privacy is not a settings page. The system has to make the trust boundary visible and enforceable by design.',
                        },
                        {
                            title: 'Continuity layer',
                            body: 'Continuity keeps the product useful across direct interaction, desk workflows, and trusted environments instead of reducing it to one isolated surface.',
                        },
                        {
                            title: 'Trusted extension layer',
                            body: 'The system must expand when necessary without abandoning user control, which is why trusted extension is a core part of the architecture rather than an afterthought.',
                        },
                    ]}
                />
                </div>
                <p className="text-body section-internal-link">
                    See how the trust boundary maps to <Link to="/security">privacy architecture</Link>. Company thesis and founder on the <Link to="/about">About page</Link>.
                </p>
            </ContentSection>

            <ContentSection
                sectionId="execution-paths"
                eyebrow="Execution Paths"
                title="Three paths. One governing logic."
                intro="Every AI task takes a path. The architecture exists because that path should be named, visible, and chosen — not hidden inside the product."
            >
                <ExecutionPathDiagram
                    modes={[
                        { label: 'Local mode', detail: 'Privacy anchor', accent: true },
                        { label: 'Trusted extension', detail: 'Private infrastructure' },
                        { label: 'Controlled external', detail: 'Explicit, bounded' },
                    ]}
                    caption="All three modes originate from a single control model. No path operates outside governance."
                />
                <CardGrid
                    pattern="meta"
                    items={[
                        {
                            title: 'Local mode',
                            body: 'Tasks that fit the local environment should stay there first. This is the anchor for privacy, responsiveness, and sovereignty.',
                        },
                        {
                            title: 'Trusted extension mode',
                            body: 'Heavier or broader tasks can extend into systems the user trusts and controls, which expands capability without giving away the control model.',
                        },
                        {
                            title: 'Controlled external path',
                            body: 'When an outside service is needed, that path should be explicit and bounded rather than hidden as the default architecture behind the product.',
                        },
                    ]}
                />
            </ContentSection>

            <ContentSection
                sectionId="local-first-rationale"
                eyebrow="Local-First — Design Rationale"
                title="Why local-first exists. Where current limits sit."
                intro="Local-first is the architectural answer to cloud dependence, opaque routing, and fragile privacy assumptions. This section is precise about both."
            >
                <div className="ed-meta">
                    {[
                        {
                            tag: 'Rationale',
                            title: 'Why local-first exists',
                            body: "Local-first is the system's answer to cloud dependence, opaque routing, latency, and fragile privacy assumptions. It is the architectural default that makes the rest of the product credible.",
                            list: [
                                'Keep suitable work close to the user.',
                                'Reduce unnecessary exposure of private context.',
                                'Preserve control over how intelligence is invoked.',
                            ],
                        },
                        {
                            tag: 'Continuity',
                            title: 'Why continuity matters',
                            body: 'The system is not useful if control survives only on one surface. Continuity is part of the architecture because serious work moves between direct interaction, desk workflows, and other trusted environments.',
                        },
                        {
                            tag: 'Mediation',
                            title: 'Where mediation fits',
                            body: "The public story can credibly describe orchestration direction, controlled execution paths, and current coordination logic. Deeper mediation and hardened sandboxing remain important ongoing product work rather than present-tense completion claims.",
                            list: [
                                'Current direction: explicit orchestration and routing logic.',
                                'Later maturity: deeper mediation, controls, and trust tooling.',
                            ],
                        },
                    ].map((item, idx) => (
                        <article key={item.title} className="ed-meta__card">
                            <div className="ed-meta__head">
                                <span className="ed-meta__index">
                                    {String(idx + 1).padStart(2, '0')}
                                </span>
                                <span className="ed-meta__tag">{item.tag}</span>
                            </div>
                            <h3 className="ed-meta__title">{item.title}</h3>
                            <p className="ed-meta__body">{item.body}</p>
                            {item.list ? (
                                <ul className="ed-meta__list">
                                    {item.list.map((line, i) => (
                                        <li
                                            key={line}
                                            className="ed-meta__list-item"
                                            data-tag={`· ${String(i + 1).padStart(2, '0')}`}
                                        >
                                            {line}
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </article>
                    ))}
                </div>
            </ContentSection>

            <ContentSection
                sectionId="system-evidence"
                eyebrow="System Evidence — Real Now"
                title="What the system contains today."
                intro="Not the roadmap. Not prototype optimism. What actually exists."
            >
                {/* Class 1 — Real System Proof
                    This block shows current architecture reality.
                    Do not add future capabilities here. Do not claim prototype work as finished.
                    Expand this block as real evidence grows — screenshots, outputs, system captures. */}
                <ProofBlock
                    title="Current system evidence"
                    truthClass={CLAIM_TRUTH_CLASSES.REAL_NOW}
                    evidence={
                        <ul className="surface-card__list">
                            <li className="text-body">Local AI orchestration backend with working execution and routing logic</li>
                            <li className="text-body">Trust-boundary architecture with privacy-layer separation enforced at the system level</li>
                            <li className="text-body">PC-side continuity with foundational cross-surface coordination</li>
                            <li className="text-body">Execution mode routing across local, trusted extension, and controlled external paths</li>
                        </ul>
                    }
                    caption="The architecture is real. Execution depth and production polish are ongoing work. This block will expand as real evidence becomes public."
                />
                <p className="text-body section-internal-link">
                    How the trust boundary maps to privacy posture on the <Link to="/security">Security page</Link>.
                </p>
            </ContentSection>

            <ContentSection
                sectionId="claim-classification"
                eyebrow="Claim Classification"
                title="Architecture earns trust when it stays precise."
            >
                <TruthStateGrid
                    items={[
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.REAL_NOW,
                            title: 'Real architecture foundations',
                            description:
                                'The real present-tense layer is the control architecture itself: local-first execution, trust boundaries, orchestration logic, and continuity as a system decision.',
                        },
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.PROTOTYPE_NOW,
                            title: 'Current coordination behavior',
                            description:
                                'The current operating flow across direct interaction, local AI, and trusted extension exists in prototype form and is still being shaped into a more mature product behavior.',
                        },
                        {
                            truthClass: CLAIM_TRUTH_CLASSES.ROADMAP,
                            title: 'Deeper mediation and platform breadth',
                            description:
                                "Expanded automation, broader compatibility, and later trust-control maturity belong to the roadmap and should stay separated from today's proof layer.",
                        },
                    ]}
                />
                <p className="text-body section-internal-link">
                    Current privacy posture on the <Link to="/security">Security page</Link>. Common questions on <Link to="/faq">FAQ</Link>.
                </p>
            </ContentSection>

            <ContentSection
                sectionId="read-path"
                eyebrow="Technical Read Path"
                title="Follow the architecture into trust and use."
            >
                <LinkRow
                    links={[
                        { label: 'Security & Privacy', to: '/security' },
                        { label: 'Pricing', to: '/pricing' },
                        { label: 'Use Cases', to: '/use-cases' },
                        { label: 'FAQ', to: '/faq' },
                    ]}
                />
            </ContentSection>
        </PageFrame>
    );
};

export default TechnologyPage;
